import React, { useEffect, useState } from "react";
import { Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ChevronDown, SquarePen } from "lucide-react";
import { SidebarMenuCardProps } from "./types";
import {
  CardContainer,
  CardTopRow,
  CardIconWrapper,
  ActionRow,
  ChildrenContainer,
  ChildItem,
  CardDivider,
} from "./style";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import {
  getNavActionTestProps,
  getNavItemTestProps,
  withNavIconTestProps,
} from "@components/SidebarCst/testIds";

const SidebarMenuCardCst: React.FC<SidebarMenuCardProps> = ({
  icon,
  title,
  description,
  to,
  active = false,
  disabled = false,
  testKey,
  actionButton,
  onClick,
  children,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const hasActiveChild = !!children?.some((child) => child.active);
  const [expanded, setExpanded] = useState(hasActiveChild);
  const hasChildren = children && children.length > 0;

  useEffect(() => {
    if (hasActiveChild) setExpanded(true);
  }, [hasActiveChild]);

  const handleClick = () => {
    if (disabled) return;
    if (hasChildren) {
      setExpanded((prev) => !prev);
      return;
    }
    if (onClick) {
      onClick();
    } else if (to) {
      navigate(to);
    }
  };

  const handleChildClick = (
    e: React.MouseEvent,
    child: { to?: string; onClick?: () => void },
  ) => {
    e.stopPropagation();
    if (child.onClick) {
      child.onClick();
    } else if (child.to) {
      navigate(child.to);
    }
  };

  return (
    <CardContainer
      active={active ? "true" : "false"}
      className={disabled ? "disabled" : ""}
      onClick={handleClick}
      {...getNavItemTestProps(testKey, title)}
    >
      <CardTopRow>
        {icon && (
          <CardIconWrapper>
            {withNavIconTestProps(icon, testKey)}
          </CardIconWrapper>
        )}
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "13px",
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {title}
        </Typography>
        {hasChildren && (
          <ChevronDown
            size={16}
            color={theme.palette.text.tertiary}
            style={{
              flexShrink: 0,
              transition: "transform 0.2s ease",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        )}
      </CardTopRow>
      {description && (
        <Typography
          sx={{
            fontSize: "11px",
            lineHeight: 1.4,
            color: theme.palette.text.description,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            paddingLeft: "30px",
          }}
        >
          {description}
        </Typography>
      )}
      {actionButton && (
        <ActionRow className="sidebar-card-action-row">
          <ButtonPrimary
            {...getNavActionTestProps(testKey)}
            size="sm"
            startIcon={<SquarePen size={12} />}
            disabled={actionButton.disabled}
            onClick={(e) => {
              e.stopPropagation();
              if (actionButton.disabled) return;
              actionButton.onClick();
            }}
          >
            {actionButton.label}
          </ButtonPrimary>
        </ActionRow>
      )}
      {hasChildren && (
        <ChildrenContainer
          sx={{
            maxHeight: expanded ? `${children.length * 32 + 24}px` : "0px",
            opacity: expanded ? 1 : 0,
          }}
        >
          <CardDivider />
          {children.map((child, index) => (
            <ChildItem
              key={index}
              onClick={(e) => handleChildClick(e, child)}
              {...getNavItemTestProps(child.testKey, child.title)}
              sx={
                child.active
                  ? { backgroundColor: theme.palette.action.selected }
                  : undefined
              }
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: child.active ? 600 : 400,
                  color: theme.palette.text.primary,
                  lineHeight: 1.4,
                }}
              >
                {child.title}
              </Typography>
            </ChildItem>
          ))}
        </ChildrenContainer>
      )}
    </CardContainer>
  );
};

export default SidebarMenuCardCst;
